import { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import Pagination from "../components/ui/Pagination";
import Modal from "../components/ui/Modal";
import { fetchWithAdminAuth } from "../utils/adminAuth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Menu, Eye } from 'lucide-react';

interface SessionRow {
  id: string;
  createdAt: string;
  user: {
    name: string;
    email: string;
  };
  consultant: {
    name: string;
  };
  status: string;
  duration: string;
}

interface ConversationDetail {
  id: string;
  status: string;
  consultantName: string;
  duration: number;
  maxDuration: number;
  paidClient: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  aiCost: number;
  startDate: string;
  endDate: string | null;
  user: { id: string; name: string; email: string; credits: number };
  consultant: { id: string; name: string; type: string[]; pricePerMin: number };
  messages: { id: string; role: string; content: string; createdAt: string }[];
}

export default function Sessoes() {
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stateFilter, setStateFilter] = useState<
    "all" | "active" | "completed" | "pending"
  >("all");
  const [dateFilter, setDateFilter] = useState(
    new Date().toISOString().slice(0, 10),
  );
  const [queryFilter, setQueryFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Conversation detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<ConversationDetail | null>(null);

  const PAGE_SIZE = 20;

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams();
        params.set("page", String(currentPage));
        params.set("limit", String(PAGE_SIZE));

        if (stateFilter !== "all") {
          params.set("state", stateFilter);
        }

        if (dateFilter.trim()) {
          params.set("date", dateFilter.trim());
        }

        if (queryFilter.trim()) {
          params.set("search", queryFilter.trim());
        }

        const response = await fetchWithAdminAuth(
          `/api/admin/getAllSessionsWithPagination?${params.toString()}`,
          { method: "GET" },
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch sessions: ${response.statusText}`);
        }

        const data = await response.json();
        const sessionsList = Array.isArray(data?.sessions) ? data.sessions : [];
        const pagination = data?.pagination || {};

        setSessions(sessionsList);

        const backendPage = Number(pagination?.page ?? data?.page ?? currentPage);
        const backendLimit = Number(pagination?.limit ?? data?.limit ?? PAGE_SIZE);
        const backendTotal = Number(
          pagination?.totalSessions ?? pagination?.total ?? data?.total ?? data?.totalItems ?? sessionsList.length,
        );
        const backendTotalPages = Number(
          pagination?.totalPages ?? data?.totalPages ?? Math.max(1, Math.ceil(backendTotal / Math.max(backendLimit, 1))),
        );

        setCurrentPage(Number.isFinite(backendPage) && backendPage > 0 ? backendPage : 1);
        setTotalItems(Number.isFinite(backendTotal) && backendTotal >= 0 ? backendTotal : sessionsList.length);
        setTotalPages(
          Number.isFinite(backendTotalPages) && backendTotalPages > 0 ? Math.floor(backendTotalPages) : 1,
        );
      } catch (err) {
        console.error("Error fetching sessions:", err);
        setError(err instanceof Error ? err.message : "Failed to load sessions");
        setSessions([]);
        setTotalItems(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    fetchSessions();
  }, [currentPage, dateFilter, queryFilter, stateFilter]);

  const paginatedSessions = useMemo(() => sessions, [sessions]);

  const openDetail = async (sessionId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await fetchWithAdminAuth(`/api/admin/conversations/${sessionId}`);
      if (res.ok) {
        const data = await res.json();
        setDetail(data);
      }
    } catch (err) {
      console.error("Error loading conversation detail:", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const getEstadoLabel = (estado: SessionRow["status"]) => {
    if (estado === "active") return "Em curso";
    if (estado === "completed") return "Terminada";
    return "Pendente";
  };

  const getEstadoClass = (estado: SessionRow["status"]) => {
    if (estado === "active") {
      return "inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30";
    }
    if (estado === "completed") {
      return "inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-300 border border-slate-500/30";
    }
    return "inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30";
  };

  return (
    <div className="px-3 sm:px-6 py-4 sm:py-5">
      <Card
        title="Sessões & Chat"
        subtitle="Monitorização das sessões em tempo real e acesso ao histórico de chat."
      >
        <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">
              Cliente email ou chatId
            </label>
            <input
              type="text"
              value={queryFilter}
              onChange={(e) => {
                setQueryFilter(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="email@dominio.com ou chat_123"
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            />
          </div>
          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">
              Estado
            </label>
            <select
              value={stateFilter}
              onChange={(e) => {
                setStateFilter(
                  e.target.value as "all" | "active" | "completed" | "pending",
                );
                setCurrentPage(1);
              }}
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            >
              <option value="all">Todos</option>
              <option value="active">Em curso</option>
              <option value="completed">Terminada</option>
              <option value="pending">Pendente</option>
            </select>
          </div>

          <div>
            <label className="block text-[0.75rem] text-text-soft mb-1.5">
              Data
            </label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full h-10 px-3 rounded-xl border border-border-soft bg-[#0a0b14]/80 text-text text-sm outline-none"
            />
          </div>
        </div>

        <div className="mb-3 text-[0.75rem] text-text-soft">
          A mostrar {paginatedSessions.length} de {totalItems}{" "}
          sessões
        </div>

        {error && (
          <div className="mb-3 text-[0.75rem] text-red-400">Erro ao carregar sessões: {error}</div>
        )}

        <div className="rounded-xl border border-border-soft overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data/hora</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Consultor</TableHead>
                <TableHead>Duração</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead><Menu width={16} /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-text-soft">A carregar sessões...</TableCell>
                </TableRow>
              ) : paginatedSessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-text-soft">
                    Nenhuma sessão encontrada
                  </TableCell>
                </TableRow>
              ) : (
                paginatedSessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      {new Date(session.createdAt).toLocaleString("pt-PT", {
                        day: "2-digit",
                        month: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>{session.user.name}</TableCell>
                    <TableCell>{session.user.email}</TableCell>
                    <TableCell>IA</TableCell>
                    <TableCell>{session.consultant.name}</TableCell>
                    <TableCell>{session.duration}</TableCell>
                    <TableCell>
                      <span className={getEstadoClass(session.status)}>
                        {getEstadoLabel(session.status)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => openDetail(session.id)}
                        className="text-[#60a5fa] hover:text-white transition-colors flex items-center gap-1 text-xs"
                      >
                        <Eye width={14} /> Detalhes
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={PAGE_SIZE}
          itemsOnPage={sessions.length}
          onPageChange={setCurrentPage}
        />
      </Card>

      {/* Conversation Detail Modal */}
      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title="Detalhes da Conversa"
      >
        {detailLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
          </div>
        ) : detail ? (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Info grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[0.78rem]">
              <div>
                <span className="text-text-soft block">Cliente</span>
                <span className="text-text font-medium">{detail.user.name}</span>
                <span className="text-text-soft block text-xs">{detail.user.email}</span>
              </div>
              <div>
                <span className="text-text-soft block">Consultor</span>
                <span className="text-text font-medium">{detail.consultantName}</span>
              </div>
              <div>
                <span className="text-text-soft block">Estado</span>
                <span className={getEstadoClass(detail.status)}>
                  {getEstadoLabel(detail.status)}
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Duração</span>
                <span className="text-text font-medium">{formatDuration(detail.duration)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Pago pelo cliente</span>
                <span className="text-text font-medium">€{detail.paidClient.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Custo IA</span>
                <span className="text-text font-medium">${detail.aiCost.toFixed(4)}</span>
              </div>
              <div>
                <span className="text-text-soft block">Tokens usados</span>
                <span className="text-text font-medium">{detail.totalTokens.toLocaleString()}</span>
                <span className="text-text-soft block text-xs">
                  {detail.promptTokens.toLocaleString()} in / {detail.completionTokens.toLocaleString()} out
                </span>
              </div>
              <div>
                <span className="text-text-soft block">Início</span>
                <span className="text-text font-medium text-xs">
                  {new Date(detail.startDate).toLocaleString("pt-PT")}
                </span>
              </div>
            </div>

            {/* Messages */}
            <div>
              <h4 className="text-[0.82rem] font-medium text-text mb-2 border-t border-border-soft pt-3">
                Mensagens ({detail.messages.length})
              </h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {detail.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`rounded-lg px-3 py-2 text-[0.75rem] ${
                      msg.role === 'user'
                        ? 'bg-purple-500/10 border border-purple-500/20 ml-8'
                        : 'bg-white/5 border border-border-soft mr-8'
                    }`}
                  >
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-medium text-[0.7rem] ${
                        msg.role === 'user' ? 'text-purple-300' : 'text-green-300'
                      }`}>
                        {msg.role === 'user' ? '👤 Cliente' : '🤖 Consultor IA'}
                      </span>
                      <span className="text-text-soft text-[0.65rem]">
                        {new Date(msg.createdAt).toLocaleTimeString("pt-PT", { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-text whitespace-pre-wrap">{msg.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-text-soft">Conversa não encontrada</div>
        )}
      </Modal>
    </div>
  );
}
